import { useEffect, useState } from 'react';
import { TemplateScene } from '@/types/cmsTypes';

// Creator affiliate links carry ?template=<scene_origin_id>. The id is parked here on
// landing so it survives signup, email confirmation and the OAuth round-trip, then the
// dashboard opens that template once and clears it.
const STORAGE_KEY = 'lureo_pending_template';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const TEMPLATE_PARAM = 'template';
// Lets the dashboard and any closing onboarding dialog settle before the modal opens.
const OPEN_DELAY_MS = 500;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface PendingTemplate {
  id: string;
  savedAt: number;
}

export function capturePendingTemplateLink(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get(TEMPLATE_PARAM)?.trim();
    if (!id || !UUID_PATTERN.test(id)) return;
    const record: PendingTemplate = { id, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));

    // Drop the consumed param so a refresh does not re-arm the template after it opened.
    params.delete(TEMPLATE_PARAM);
    const search = params.toString();
    const url = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', url);
  } catch {
    // Best-effort: a blocked localStorage just means the template does not auto-open.
  }
}

function getPendingTemplateId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingTemplate;
    if (!parsed?.id || Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.id;
  } catch {
    return null;
  }
}

function clearPendingTemplate(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Opens the template from a creator link once the dashboard is ready. `blocked` holds it
 * back (e.g. while the onboarding questions are pending) so it lands after them.
 * `arrivedViaTemplateLink` stays true for the visit so callers can suppress the tour,
 * unless the linked template turns out not to exist.
 */
export function usePendingTemplateLink({
  templateScenes,
  templatesLoading,
  blocked,
  onOpen
}: {
  templateScenes: TemplateScene[];
  templatesLoading: boolean;
  blocked: boolean;
  onOpen: (template: TemplateScene) => void;
}) {
  const [pendingId, setPendingId] = useState(getPendingTemplateId);
  const [arrivedViaTemplateLink, setArrivedViaTemplateLink] = useState(() => pendingId !== null);
  const [discarded, setDiscarded] = useState(false);

  // A linked template that does not exist is dropped as soon as the list has loaded.
  const ready = !!pendingId && !blocked && !templatesLoading && templateScenes.length > 0;
  if (ready && !templateScenes.some((t) => t.id === pendingId)) {
    setPendingId(null);
    setArrivedViaTemplateLink(false);
    setDiscarded(true);
  }

  useEffect(() => {
    if (discarded) clearPendingTemplate();
  }, [discarded]);

  useEffect(() => {
    if (!pendingId || blocked || templatesLoading || templateScenes.length === 0) return;
    const template = templateScenes.find((t) => t.id === pendingId);
    if (!template) return;
    const timer = setTimeout(() => {
      clearPendingTemplate();
      setPendingId(null);
      onOpen(template);
    }, OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pendingId, blocked, templatesLoading, templateScenes, onOpen]);

  return { arrivedViaTemplateLink };
}
